import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavType } from '../../const';
import UnityRenderMap from '../UnityRenderMap';
import type { RenderStreamingVideoElement, UnityCall, UnityCameraData, UnityClickDetail } from '../UnityRenderMap';
import { UNITY_CAMERA_CONFIG } from '../UnityRenderMap/cameraConfig';
import type { BusinessCleanupKey } from '../UnityRenderMap/businessCleanup';
import styles from './index.module.scss';

type SharedUnityHelpers = {
  callUnity: UnityCall;
  videoElement: RenderStreamingVideoElement;
};

type UnityClickHandler = (detail: UnityClickDetail) => void;

type SharedUnityMapContextValue = {
  unityReady: boolean;
  callUnity?: UnityCall;
  videoElement?: RenderStreamingVideoElement;
  registerUnityClickHandler: (handler?: UnityClickHandler) => () => void;
};

const SharedUnityMapContext = createContext<SharedUnityMapContextValue>({
  unityReady: false,
  registerUnityClickHandler: () => () => {},
});

const UNITY_NAV_CAMERA_CONFIG: Partial<Record<NavType, UnityCameraData[]>> = {
  [NavType.PowerMap]: UNITY_CAMERA_CONFIG.unityPower,
  [NavType.LightingMap]: UNITY_CAMERA_CONFIG.light,
  [NavType.WaterMap]: UNITY_CAMERA_CONFIG.water,
  [NavType.EnergyCockpit]: UNITY_CAMERA_CONFIG.energyCockpit,
  [NavType.IndustrialGasMap]: UNITY_CAMERA_CONFIG.gas,
  [NavType.EnvironmentalMonitor]: UNITY_CAMERA_CONFIG.environment,
};

const UNITY_NAV_BUSINESS_KEYS: Partial<Record<NavType, BusinessCleanupKey[]>> = {
  [NavType.PowerMap]: ['powerArea'],
  [NavType.EnergyCockpit]: ['energyOverview'],
  [NavType.WaterMap]: ['waterStation', 'waterFlowmeter'],
  [NavType.EnvironmentalMonitor]: ['environmentMonitor'],
  [NavType.LightingMap]: ['lightGgd', 'lightSun', 'lightWorkshop'],
};

interface SharedUnityMapProviderProps {
  currentNav: NavType;
  enabled: boolean;
  children: React.ReactNode;
}

export const useSharedUnityMap = () => useContext(SharedUnityMapContext);

export const UnityOverlay: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...restProps }) => (
  <div className={`${styles.unityOverlay} ${className ?? ''}`} {...restProps}>
    {children}
  </div>
);

const SharedUnityMapProvider: React.FC<SharedUnityMapProviderProps> = ({ currentNav, enabled, children }) => {
  const [helpers, setHelpers] = useState<SharedUnityHelpers | null>(null);
  const [unityReady, setUnityReady] = useState(false);
  const clickHandlerRef = useRef<UnityClickHandler | undefined>(undefined);
  const cameraData = UNITY_NAV_CAMERA_CONFIG[currentNav];
  const businessCleanupKeys = useMemo(() => UNITY_NAV_BUSINESS_KEYS[currentNav] ?? [], [currentNav]);
  const shouldRenderUnity = enabled && !!cameraData;

  const registerUnityClickHandler = useCallback((handler?: UnityClickHandler) => {
    clickHandlerRef.current = handler;
    return () => {
      if (clickHandlerRef.current === handler) {
        clickHandlerRef.current = undefined;
      }
    };
  }, []);

  const handleUnityReady = useCallback((nextHelpers: SharedUnityHelpers) => {
    setHelpers(nextHelpers);
    setUnityReady(true);
  }, []);

  const handleUnityClick = useCallback((detail: UnityClickDetail) => {
    clickHandlerRef.current?.(detail);
  }, []);

  useEffect(() => {
    if (!shouldRenderUnity || !unityReady || !helpers?.callUnity || !cameraData?.length) return;

    console.info('[SharedUnityMap] setting camera', { currentNav, cameraData });
    helpers
      .callUnity({
        type: 'setCamera',
        data: cameraData,
      })
      .then(() => {
        console.info('[SharedUnityMap] camera set', { currentNav });
      })
      .catch(error => {
        console.error('[SharedUnityMap] camera set failed', error);
      });
  }, [cameraData, currentNav, helpers, shouldRenderUnity, unityReady]);

  const contextValue = useMemo<SharedUnityMapContextValue>(
    () => ({
      unityReady: shouldRenderUnity && unityReady,
      callUnity: helpers?.callUnity,
      videoElement: helpers?.videoElement,
      registerUnityClickHandler,
    }),
    [helpers?.callUnity, helpers?.videoElement, registerUnityClickHandler, shouldRenderUnity, unityReady]
  );

  return (
    <SharedUnityMapContext.Provider value={contextValue}>
      {shouldRenderUnity && (
        <div className={styles.sharedUnityMap}>
          <UnityRenderMap
            className={styles.sharedUnityMapInner}
            initialCameraData={null}
            businessCleanupKeys={businessCleanupKeys}
            enableBridge
            onUnityClick={handleUnityClick}
            onReady={handleUnityReady}
          />
        </div>
      )}
      {children}
    </SharedUnityMapContext.Provider>
  );
};

export default SharedUnityMapProvider;
